import React from 'react';
import { Link } from 'react-router-dom';
import { FireIcon } from '@heroicons/react/24/outline';
import Pill from '../common/display/Pill';
import { Button } from '../common/ui/Button';
import Avatar from '../common/display/Avatar';
import { CommunityMember } from './types';
import { captureCustomEvent } from '../../utils/posthogUtils';

interface FeaturedCommunityCardProps {
  community: {
    id: string;
    name: string;
    description: string;
    header_image?: string;
    avatar_image?: string;
    members?: CommunityMember[];
    totalMembers?: number;
    workspace?: {
      workspace_id?: string;
      workspace_name: string;
      logo_url?: string;
    };
  };
  onViewCommunity?: () => void;
}

const FeaturedCommunityCard: React.FC<FeaturedCommunityCardProps> = ({ 
  community,
  onViewCommunity 
}) => {
  const members = community.members || [];
  const totalMembers = community.totalMembers ?? members.length;
  const maxVisibleAvatars = 5;
  const displayedMembers = members.slice(0, maxVisibleAvatars);
  const remainingMembers = totalMembers - maxVisibleAvatars;

  const handleViewCommunity = () => {
    captureCustomEvent('featured_community_view_clicked', {
      eventProperties: {
        community_id: community.id,
        community_name: community.name,
        total_members: totalMembers,
        has_workspace: !!community.workspace,
        source: 'featured_community_card'
      }
    });
    
    onViewCommunity?.();
  };

  return (
    <div className="h-full block">
      <div className="bg-gray-900 rounded-2xl overflow-hidden flex flex-col h-full group relative">
        {/* Background shape */}
        <div className="absolute right-0 top-0 bottom-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 h-full w-auto flex justify-end items-center">
            <img 
              src="https://azzcpbslaipddvocsrcx.supabase.co/storage/v1/object/public/assets//event-combined-shape.svg" 
              alt="" 
              className="h-full w-auto object-contain object-right transition-transform duration-300 ease-in-out transform-gpu scale-100 group-hover:scale-110 origin-[75%_50%]"
            />
          </div>
        </div>
        
        <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 flex flex-col justify-between flex-grow relative z-10">
          <div className="flex-grow">
            <div className="flex justify-between items-start">
              <div className="flex flex-col max-w-[70%] sm:max-w-[75%]">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  <div className="flex items-center gap-3">
                    {community.avatar_image && (
                      <Avatar 
                        firstName={community.name}
                        profileImageUrl={community.avatar_image}
                        size="medium"
                        className="shrink-0"
                      />
                    )}
                    <span className="min-w-0 break-words">{community.name}</span>
                  </div>
                </h3>
                
                <div className="flex flex-wrap gap-2 mt-4 mb-1">
                  <Pill
                    text={`${totalMembers} Members`}
                    className="bg-blue-100 text-blue-600 text-sm"
                  />
                  <div className="flex items-center gap-1 text-sm font-bold text-tertiary-base">
                    <FireIcon className="h-4 w-4" />
                    <span>Featured Community</span>
                  </div>
                </div>
              </div>

              <div className="flex -space-x-2">
                {displayedMembers.map((member) => (
                  <Avatar
                    key={member.user_id}
                    firstName={member.first_name || ''}
                    lastName={member.last_name || ''}
                    profileImageUrl={member.profile_image_url || undefined}
                    size="small"
                    className="border-2 border-gray-900 w-10 h-10"
                  />
                ))}
                {remainingMembers > 0 && (
                  <div className="relative w-10 h-10 flex items-center justify-center bg-gray-800 text-white text-xs font-bold rounded-full border-2 border-gray-900">
                    +{remainingMembers}
                  </div>
                )}
              </div>
            </div>

            <p className="mt-4 line-clamp-3 break-words text-gray-200 text-sm sm:text-base sm:max-w-[95%] md:max-w-[90%]">
              {community.description}
            </p>

            {community.workspace && (
              <div className="mt-4 flex items-center gap-2">
                <div className="flex items-center gap-2">
                  {community.workspace.logo_url && (
                    <Avatar
                      firstName={community.workspace.workspace_name}
                      profileImageUrl={community.workspace.logo_url}
                      size="tiny"
                    />
                  )}
                  <Link 
                    to={`/workspace/${community.workspace.workspace_id}`}
                    className="text-sm text-gray-200 hover:text-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    by {community.workspace.workspace_name}
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 pb-3">
            <Link to={`/communities/${community.id}`} onClick={handleViewCommunity}>
              <Button
                variant="filled"
                color="primary"
                size="small"
                className="w-fit font-medium text-white"
              >
                View Community
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedCommunityCard; 